import * as i2$1 from '@angular/cdk/observers';
import { ObserversModule } from '@angular/cdk/observers';
import * as i1$1 from '@angular/cdk/overlay';
import { OverlayContainer, Overlay, OverlayModule } from '@angular/cdk/overlay';
import * as i3 from '@angular/common';
import { DOCUMENT, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Component, ChangeDetectionStrategy, Inject, Input, HostBinding, Directive, Self, Optional, Host, SkipSelf, Injectable, HostListener, NgModule } from '@angular/core';
import * as i1 from '@angular/cdk/a11y';
import { take, delay, takeUntil, map, filter } from 'rxjs/operators';
import { MFWKError } from '@mercadona/core/utils/error';
import * as i2 from '@mercadona/components/button';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { ComponentPortal } from '@angular/cdk/portal';
import { Subject } from 'rxjs';
import * as i4 from '@angular/cdk/bidi';

const defaultSpinnerSize = 'default';
const defaultSpinnerColor = 'main';

class MSpinnerComponent {
    constructor(_document, _renderer, _cdr, _elementRef, _configurableFocusTrapFactory) {
        this._document = _document;
        this._renderer = _renderer;
        this._cdr = _cdr;
        this._elementRef = _elementRef;
        this._configurableFocusTrapFactory = _configurableFocusTrapFactory;
        this.disableFocus = false;
        this.size = defaultSpinnerSize;
        this.color = defaultSpinnerColor;
    }
    get class() {
        return `m-spinner-${this.size || defaultSpinnerSize} m-spinner-${this.color}-color`;
    }
    get message() {
        return this._message;
    }
    set message(message) {
        this._message = message;
        // Forces change detection because Portal CDK does not trigger onChanges
        // (cdr.detectChanges() throws an 'ASSERTION ERROR' with Ivy)
        this._cdr.markForCheck();
    }
    ngOnInit() {
        if (this.disableFocus) {
            this.initFocusTrap();
        }
    }
    ngOnDestroy() {
        if (this.disableFocus) {
            this.restoreFocus();
        }
    }
    initFocusTrap() {
        this.previousFocusedElement = this._document.activeElement;
        this._renderer.setAttribute(this._elementRef.nativeElement, 'tabindex', '0');
        this.focusTrap = this._configurableFocusTrapFactory.create(this._elementRef.nativeElement);
        this.focusTrap.focusInitialElementWhenReady();
    }
    restoreFocus() {
        this._renderer.removeAttribute(this._elementRef.nativeElement, 'tabindex');
        this.previousFocusedElement?.focus();
        this.focusTrap?.destroy();
    }
}
MSpinnerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MSpinnerComponent, deps: [{ token: DOCUMENT }, { token: i0.Renderer2 }, { token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i1.ConfigurableFocusTrapFactory }], target: i0.ɵɵFactoryTarget.Component });
MSpinnerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.9", type: MSpinnerComponent, selector: "m-spinner", inputs: { size: "size", color: "color", message: "message" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: "<svg class=\"m-spinner-icon\" viewBox=\"0 0 100 100\" xmlns=\"http://www.w3.org/2000/svg\">\n  <circle cx=\"50\" cy=\"50\" fill=\"none\" r=\"40\" stroke-width=\"16\"></circle>\n</svg>\n\n<span *ngIf=\"message\" class=\"m-spinner-message\">{{ message }}</span>\n", styles: ["::ng-deep .m-spinner-container{transform:translateZ(0);pointer-events:none!important;-webkit-user-select:none!important;user-select:none!important;cursor:default!important}::ng-deep .m-spinner-main-backdrop{background:var(--onColorSurface_Low);transition:background-color .5s ease}::ng-deep .m-spinner-white-backdrop{background:var(--spinnerWhiteBackdropBackground);transition:background-color .5s ease}::ng-deep .m-spinner-panel{pointer-events:none}:host{display:flex;justify-content:center;align-items:center;pointer-events:none;-webkit-user-select:none;user-select:none;outline:none}:host.m-spinner-default{flex-direction:column}:host.m-spinner-default .m-spinner-message{margin-top:16px}:host.m-spinner-default .m-spinner-icon{width:88px;height:88px;transition:width .2s ease-out,height .2s ease-out}:host.m-spinner-small{flex-direction:row-reverse}:host.m-spinner-small .m-spinner-message{margin-right:16px}:host.m-spinner-small .m-spinner-icon{width:16px;height:16px;transition:width .2s ease-out,height .2s ease-out}:host .m-spinner-icon{animation:4.5s linear rotate infinite}:host .m-spinner-icon circle{animation:1.5s ease-out spinning infinite;transition:stroke .5s ease}:host .m-spinner-message{font-weight:var(--fontWeightSemibold);font-size:var(--fontSizeBody03);line-height:var(--lineHeightBody03)}:host.m-spinner-main-color .m-spinner-icon circle{stroke:var(--main)}:host.m-spinner-main-color .m-spinner-message{color:var(--onSurface_High)}:host.m-spinner-white-color .m-spinner-icon circle{stroke:var(--spinnerWhiteBackgroundColor)}:host.m-spinner-white-color .m-spinner-message{color:var(--spinnerWhiteMessageColor)}@keyframes spinning{0%{stroke-dasharray:1,400;stroke-dashoffset:0}50%{stroke-dasharray:179.69,400;stroke-dashoffset:-70.37}to{stroke-dasharray:179.69,400;stroke-dashoffset:-249.31}}@keyframes rotate{to{transform:rotate(360deg)}}\n"], dependencies: [{ kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MSpinnerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'm-spinner', changeDetection: ChangeDetectionStrategy.OnPush, template: "<svg class=\"m-spinner-icon\" viewBox=\"0 0 100 100\" xmlns=\"http://www.w3.org/2000/svg\">\n  <circle cx=\"50\" cy=\"50\" fill=\"none\" r=\"40\" stroke-width=\"16\"></circle>\n</svg>\n\n<span *ngIf=\"message\" class=\"m-spinner-message\">{{ message }}</span>\n", styles: ["::ng-deep .m-spinner-container{transform:translateZ(0);pointer-events:none!important;-webkit-user-select:none!important;user-select:none!important;cursor:default!important}::ng-deep .m-spinner-main-backdrop{background:var(--onColorSurface_Low);transition:background-color .5s ease}::ng-deep .m-spinner-white-backdrop{background:var(--spinnerWhiteBackdropBackground);transition:background-color .5s ease}::ng-deep .m-spinner-panel{pointer-events:none}:host{display:flex;justify-content:center;align-items:center;pointer-events:none;-webkit-user-select:none;user-select:none;outline:none}:host.m-spinner-default{flex-direction:column}:host.m-spinner-default .m-spinner-message{margin-top:16px}:host.m-spinner-default .m-spinner-icon{width:88px;height:88px;transition:width .2s ease-out,height .2s ease-out}:host.m-spinner-small{flex-direction:row-reverse}:host.m-spinner-small .m-spinner-message{margin-right:16px}:host.m-spinner-small .m-spinner-icon{width:16px;height:16px;transition:width .2s ease-out,height .2s ease-out}:host .m-spinner-icon{animation:4.5s linear rotate infinite}:host .m-spinner-icon circle{animation:1.5s ease-out spinning infinite;transition:stroke .5s ease}:host .m-spinner-message{font-weight:var(--fontWeightSemibold);font-size:var(--fontSizeBody03);line-height:var(--lineHeightBody03)}:host.m-spinner-main-color .m-spinner-icon circle{stroke:var(--main)}:host.m-spinner-main-color .m-spinner-message{color:var(--onSurface_High)}:host.m-spinner-white-color .m-spinner-icon circle{stroke:var(--spinnerWhiteBackgroundColor)}:host.m-spinner-white-color .m-spinner-message{color:var(--spinnerWhiteMessageColor)}@keyframes spinning{0%{stroke-dasharray:1,400;stroke-dashoffset:0}50%{stroke-dasharray:179.69,400;stroke-dashoffset:-70.37}to{stroke-dasharray:179.69,400;stroke-dashoffset:-249.31}}@keyframes rotate{to{transform:rotate(360deg)}}\n"] }]
        }], ctorParameters: function () { return [{ type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i0.Renderer2 }, { type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i1.ConfigurableFocusTrapFactory }]; }, propDecorators: { size: [{
                type: Input
            }], color: [{
                type: Input
            }], class: [{
                type: HostBinding,
                args: ['class']
            }], message: [{
                type: Input
            }] } });

class MButtonSpinnerDirective {
    constructor(_mSpinnerComponent, _mButtonComponent, _elementRef, _renderer, _ngZone) {
        this._mSpinnerComponent = _mSpinnerComponent;
        this._mButtonComponent = _mButtonComponent;
        this._elementRef = _elementRef;
        this._renderer = _renderer;
        this._ngZone = _ngZone;
        /** Current state of the button */
        this._disabled = false;
        /** Stores the final state of the button after the spinner is destroyed */
        this._disabledState = false;
        if (this._mButtonComponent) {
            this._setDefaultButtonSpinnerInputs();
        }
        else {
            throw new MFWKError(`'m-button-spinner' directive can only be used inside a MButtonComponent element.`);
        }
    }
    ngOnInit() {
        if (this._mButtonComponent) {
            this._parentElement = this._renderer.parentNode(this._elementRef.nativeElement);
            this._observeDisabledChanges();
            this._disabledState = this._parentElement?.disabled || false;
            this._changeDisabledState(true);
            this._renderer.addClass(this._parentElement, 'm-button-spinner');
        }
    }
    ngOnDestroy() {
        if (this._mButtonComponent) {
            // When disable and spinner is set to false inside a subscription, the change detection runs at the same time.
            // With onMicrotaskEmpty waits for all microtasks (MutationObserver) and then clears the state.
            this._ngZone.onMicrotaskEmpty.pipe(take(1), delay(0)).subscribe(() => {
                this._disabledObserver?.disconnect();
                this._changeDisabledState(this._disabledState);
                this._renderer.removeClass(this._parentElement, 'm-button-spinner');
            });
        }
    }
    /** Sets default inputs to the spinner to fit inside the button */
    _setDefaultButtonSpinnerInputs() {
        this._mSpinnerComponent.size = 'small';
        this._mSpinnerComponent.color = defaultSpinnerColor;
        this._mSpinnerComponent.message = null;
    }
    /** Creates an MutationObserver for button [disabled] changes */
    _observeDisabledChanges() {
        this._disabledObserver = new MutationObserver((mutations) => {
            // Checks for disable changes when the spinner is already loaded
            if (!this._disabledState && this._disabled !== undefined && this._disabled) {
                this._disabledState = true;
            }
            // Gets the current disabled state of the button
            this._disabled = mutations[mutations.length - 1].target.disabled;
            // If the current state is not disabled because of [disabled] changes, forces to disable and
            // store the current value
            if (!this._disabled) {
                this._disabledState = this._disabled;
                this._changeDisabledState(true);
            }
        });
        if (this._parentElement) {
            this._disabledObserver.observe(this._parentElement, { attributeFilter: ['disabled'] });
        }
    }
    /**
     * Sets the button [disabled] state
     *
     * @param state
     */
    _changeDisabledState(state) {
        if (this._parentElement) {
            this._renderer.setProperty(this._parentElement, 'disabled', state);
        }
    }
}
MButtonSpinnerDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MButtonSpinnerDirective, deps: [{ token: MSpinnerComponent, self: true }, { token: i2.MButtonComponent, host: true, optional: true, skipSelf: true }, { token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Directive });
MButtonSpinnerDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.9", type: MButtonSpinnerDirective, selector: "m-spinner[m-button-spinner]", exportAs: ["m-button-spinner"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MButtonSpinnerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'm-spinner[m-button-spinner]',
                    exportAs: 'm-button-spinner'
                }]
        }], ctorParameters: function () { return [{ type: MSpinnerComponent, decorators: [{
                    type: Self
                }] }, { type: i2.MButtonComponent, decorators: [{
                    type: Optional
                }, {
                    type: Host
                }, {
                    type: SkipSelf
                }] }, { type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i0.NgZone }]; } });

class MCustomSpinnerOverlayContainer extends OverlayContainer {
    setContainerElement(containerElement) {
        this._containerElement = containerElement;
    }
}
MCustomSpinnerOverlayContainer.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MCustomSpinnerOverlayContainer, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
MCustomSpinnerOverlayContainer.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MCustomSpinnerOverlayContainer });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MCustomSpinnerOverlayContainer, decorators: [{
            type: Injectable
        }] });

class MCustomSpinnerOverlay extends Overlay {
    constructor(scrollStrategies, _overlayContainer, _componentFactoryResolver, _positionBuilder, _keyboardDispatcher, _injector, _location, _outsideClickDispatcher, _ngZone, _document, _directionality) {
        super(scrollStrategies, _overlayContainer, _componentFactoryResolver, _positionBuilder, _keyboardDispatcher, _injector, _ngZone, _document, _directionality, _location, _outsideClickDispatcher);
        this._destroy$ = new Subject();
        this._mCustomSpinnerOverlayContainer = _overlayContainer;
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
    createWithDefaultConfig(containerElement, color) {
        this._setContainerElement(containerElement);
        const positionStrategy = this.position().global().centerHorizontally().centerVertically();
        const overlayRef = super.create({
            positionStrategy,
            hasBackdrop: true,
            backdropClass: `m-spinner-${color}-backdrop`,
            panelClass: 'm-spinner-panel'
        });
        overlayRef
            .backdropClick()
            .pipe(takeUntil(this._destroy$))
            .subscribe((ev) => ev.stopPropagation());
        return overlayRef;
    }
    _setContainerElement(containerElement) {
        this._mCustomSpinnerOverlayContainer.setContainerElement(containerElement);
    }
}
MCustomSpinnerOverlay.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MCustomSpinnerOverlay, deps: [{ token: i1$1.ScrollStrategyOptions }, { token: MCustomSpinnerOverlayContainer }, { token: i0.ComponentFactoryResolver }, { token: i1$1.OverlayPositionBuilder }, { token: i1$1.OverlayKeyboardDispatcher }, { token: i0.Injector }, { token: i3.Location }, { token: i1$1.OverlayOutsideClickDispatcher }, { token: i0.NgZone }, { token: DOCUMENT }, { token: i4.Directionality }], target: i0.ɵɵFactoryTarget.Injectable });
MCustomSpinnerOverlay.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MCustomSpinnerOverlay });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MCustomSpinnerOverlay, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$1.ScrollStrategyOptions }, { type: MCustomSpinnerOverlayContainer }, { type: i0.ComponentFactoryResolver }, { type: i1$1.OverlayPositionBuilder }, { type: i1$1.OverlayKeyboardDispatcher }, { type: i0.Injector }, { type: i3.Location }, { type: i1$1.OverlayOutsideClickDispatcher }, { type: i0.NgZone }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i4.Directionality }]; } });

class MSpinnerDirective {
    constructor(_elementRef, _mCustomSpinnerOverlay, _renderer, _contentObserver) {
        this._elementRef = _elementRef;
        this._mCustomSpinnerOverlay = _mCustomSpinnerOverlay;
        this._renderer = _renderer;
        this._contentObserver = _contentObserver;
        this.isSpinnerVisible = false;
        this._elementsTabIndex = [];
        this._spinnerSize = defaultSpinnerSize;
        this._spinnerColor = defaultSpinnerColor;
    }
    set spinnerMessage(message) {
        this._spinnerMessage = message;
        this._setSpinnerInputs();
    }
    /**
     * Spinner size. Possible values: `default`, `small`
     */
    set spinnerSize(size) {
        this._spinnerSize = size || defaultSpinnerSize;
        this._setSpinnerInputs();
    }
    set mSpinner(showSpinner) {
        if (coerceBooleanProperty(showSpinner)) {
            this.show();
        }
        else {
            this.hide();
        }
    }
    set spinnerColor(color) {
        this._spinnerColor = color || defaultSpinnerColor;
        this._setBackdropClass();
        this._setSpinnerInputs();
    }
    get spinnerContainer() {
        return this.isSpinnerVisible;
    }
    /**
     * Prevents the default element actions if it is showing spinner.
     *
     * @param event
     */
    disableClick(event) {
        if (this.isSpinnerVisible) {
            event.preventDefault();
            event.stopPropagation();
        }
    }
    handleKeydown(event) {
        if (this.isSpinnerVisible && event.code !== 'Tab') {
            event.preventDefault();
            event.stopPropagation();
        }
    }
    ngAfterViewInit() {
        this._generateChildrenTabIndexes(this._elementRef.nativeElement);
        this._contentObserver
            .observe(this._elementRef)
            .pipe(map((changes) => changes.filter((change) => change.addedNodes.length > 0)), filter((changes) => changes.length > 0))
            .subscribe((changes) => {
            changes.forEach((change) => change.addedNodes.forEach((element) => this._generateChildrenTabIndexes(element)));
            if (this.isSpinnerVisible) {
                this._disableTabNavigation();
            }
        });
        if (this.isSpinnerVisible) {
            this._disableTabNavigation();
        }
    }
    ngOnDestroy() {
        this._overlayRef?.detach();
        this._overlayRef?.dispose();
        this._contentObserver.ngOnDestroy();
        this._restoreTabNavigation();
        this.isSpinnerVisible = false;
    }
    hide() {
        if (this._overlayRef?.hasAttached()) {
            this._overlayRef.detach();
            this._restoreTabNavigation();
            this.isSpinnerVisible = false;
        }
    }
    show() {
        if (!this._overlayRef) {
            this._createOverlayRef();
        }
        if (!this._overlayRef?.hasAttached()) {
            this._spinnerRef = this._overlayRef?.attach(new ComponentPortal(MSpinnerComponent));
            this._setBackdropClass();
        }
        this._setSpinnerInputs();
        this._disableTabNavigation();
        this.isSpinnerVisible = true;
    }
    _createOverlayRef() {
        this._overlayRef = this._mCustomSpinnerOverlay.createWithDefaultConfig(this._elementRef.nativeElement, this._spinnerColor);
    }
    _setSpinnerInputs() {
        if (this._overlayRef?.hasAttached() && this._spinnerRef) {
            this._spinnerRef.instance.message = this._spinnerMessage;
            this._spinnerRef.instance.size = this._spinnerSize;
            this._spinnerRef.instance.color = this._spinnerColor;
        }
    }
    _setBackdropClass() {
        const oldColorClass = this._overlayRef?.getConfig().backdropClass;
        const newColorClass = `m-spinner-${this._spinnerColor}-backdrop`;
        if (oldColorClass !== newColorClass) {
            if (oldColorClass) {
                if (this._overlayRef) {
                    this._overlayRef.backdropElement?.classList.replace(oldColorClass, newColorClass);
                    this._overlayRef.getConfig().backdropClass = newColorClass;
                }
            }
            else {
                this._overlayRef?.backdropElement?.classList.add(newColorClass);
            }
        }
    }
    _generateChildrenTabIndexes(element) {
        if (element.tabIndex > -1) {
            this._elementsTabIndex.push({
                element,
                tabIndex: element.tabIndex.toString()
            });
        }
        element.childNodes.forEach((item) => this._generateChildrenTabIndexes(item));
    }
    _disableTabNavigation() {
        this._elementsTabIndex.forEach((item) => this._renderer.setAttribute(item.element, 'tabindex', '-1'));
    }
    _restoreTabNavigation() {
        this._elementsTabIndex.forEach((item) => this._renderer.setAttribute(item.element, 'tabindex', item.tabIndex));
    }
}
MSpinnerDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MSpinnerDirective, deps: [{ token: i0.ElementRef }, { token: MCustomSpinnerOverlay }, { token: i0.Renderer2 }, { token: i2$1.ContentObserver }], target: i0.ɵɵFactoryTarget.Directive });
MSpinnerDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.9", type: MSpinnerDirective, selector: "[m-spinner]", inputs: { spinnerMessage: "spinnerMessage", spinnerSize: "spinnerSize", mSpinner: ["m-spinner", "mSpinner"], spinnerColor: "spinnerColor" }, host: { listeners: { "click": "disableClick($event)", "mousedown": "disableClick($event)", "mouseup": "disableClick($event)", "mouseover": "disableClick($event)", "mouseenter": "disableClick($event)", "keydown": "handleKeydown($event)" }, properties: { "class.m-spinner-container": "this.spinnerContainer" } }, providers: [MCustomSpinnerOverlay, MCustomSpinnerOverlayContainer], exportAs: ["m-spinner"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MSpinnerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[m-spinner]',
                    exportAs: 'm-spinner',
                    providers: [MCustomSpinnerOverlay, MCustomSpinnerOverlayContainer]
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: MCustomSpinnerOverlay }, { type: i0.Renderer2 }, { type: i2$1.ContentObserver }]; }, propDecorators: { spinnerMessage: [{
                type: Input
            }], spinnerSize: [{
                type: Input
            }], mSpinner: [{
                type: Input,
                args: ['m-spinner']
            }], spinnerColor: [{
                type: Input
            }], spinnerContainer: [{
                type: HostBinding,
                args: ['class.m-spinner-container']
            }], disableClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }, {
                type: HostListener,
                args: ['mousedown', ['$event']]
            }, {
                type: HostListener,
                args: ['mouseup', ['$event']]
            }, {
                type: HostListener,
                args: ['mouseover', ['$event']]
            }, {
                type: HostListener,
                args: ['mouseenter', ['$event']]
            }], handleKeydown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

class MSpinnerModule {
}
MSpinnerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MSpinnerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MSpinnerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.9", ngImport: i0, type: MSpinnerModule, declarations: [MSpinnerDirective, MSpinnerComponent, MButtonSpinnerDirective], imports: [CommonModule, OverlayModule, ObserversModule], exports: [MSpinnerDirective, MSpinnerComponent, MButtonSpinnerDirective] });
MSpinnerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MSpinnerModule, imports: [CommonModule, OverlayModule, ObserversModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MSpinnerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [MSpinnerDirective, MSpinnerComponent, MButtonSpinnerDirective],
                    imports: [CommonModule, OverlayModule, ObserversModule],
                    exports: [MSpinnerDirective, MSpinnerComponent, MButtonSpinnerDirective]
                }]
        }] });

class MSpinnerRef {
    constructor(_overlayRef) {
        this._overlayRef = _overlayRef;
        this._afterClosed$ = new Subject();
    }
    close() {
        this._overlayRef.detach();
        this._overlayRef.dispose();
        this._afterClosed$.next();
        this._afterClosed$.complete();
    }
    afterClosed() {
        return this._afterClosed$.asObservable();
    }
    changeColor(color) {
        const oldColorClass = this._overlayRef.getConfig().backdropClass;
        const newColorClass = `m-spinner-${color}-backdrop`;
        if (oldColorClass !== newColorClass) {
            this.componentInstance.color = color;
            this._overlayRef.backdropElement?.classList.replace(oldColorClass, newColorClass);
            this._overlayRef.getConfig().backdropClass = newColorClass;
        }
    }
}

class MSpinnerGlobalService {
    constructor(_overlay) {
        this._overlay = _overlay;
    }
    create(options) {
        const positionStrategy = this._overlay.position().global().centerHorizontally().centerVertically();
        const scrollStrategy = this._overlay.scrollStrategies.block();
        const overlayRef = this._overlay.create({
            positionStrategy,
            scrollStrategy,
            hasBackdrop: true,
            backdropClass: `m-spinner-${options?.color || 'white'}-backdrop`
        });
        return this._setContentProperties(overlayRef, options);
    }
    _setContentProperties(overlayRef, options = {}) {
        const spinnerPortal = new ComponentPortal(MSpinnerComponent);
        const spinnerAttachRef = overlayRef.attach(spinnerPortal);
        const spinnerRef = new MSpinnerRef(overlayRef);
        spinnerRef.componentInstance = spinnerAttachRef.instance;
        Object.assign(spinnerRef.componentInstance, { ...options, disableFocus: true });
        return spinnerRef;
    }
}
MSpinnerGlobalService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MSpinnerGlobalService, deps: [{ token: i1$1.Overlay }], target: i0.ɵɵFactoryTarget.Injectable });
MSpinnerGlobalService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MSpinnerGlobalService, providedIn: MSpinnerModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MSpinnerGlobalService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: MSpinnerModule
                }]
        }], ctorParameters: function () { return [{ type: i1$1.Overlay }]; } });

/**
 * Generated bundle index. Do not edit.
 */

export { MButtonSpinnerDirective, MSpinnerComponent, MSpinnerDirective, MSpinnerGlobalService, MSpinnerModule, MSpinnerRef };
